import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 30 default investing tips for beginners (used as fallback)
const DEFAULT_TIPS = [
  { title: "Start with Index Funds", content: "As a beginner, index funds like VFV (S&P 500) or XIU (TSX) give you instant diversification across hundreds of companies." },
  { title: "Pay Yourself First", content: "Set up automatic transfers to your investment account on payday. Even $50/month adds up to over $600/year." },
  { title: "TFSA vs RRSP", content: "TFSA is usually better for students earning under $50K. You don't get a tax deduction, but all growth is tax-free forever." },
  { title: "The 50/30/20 Rule", content: "Allocate 50% of income to needs, 30% to wants, and 20% to savings/investments. Adjust as needed, but have a plan." },
  { title: "Time in Market > Timing", content: "Trying to time the market rarely works. Staying invested through ups and downs historically beats jumping in and out." },
  { title: "Emergency Fund First", content: "Before investing heavily, build 3-6 months of expenses in a high-interest savings account (HISA) for emergencies." },
  { title: "Compound Interest Magic", content: "At 8% annual returns, your money doubles every 9 years. Starting at 20 vs 30 means potentially 2x more at retirement." },
  { title: "Low Fees Matter", content: "A 2% fee vs 0.2% fee over 30 years can cost you hundreds of thousands. Stick to low-cost ETFs." },
  { title: "Dollar-Cost Averaging", content: "Invest the same amount regularly regardless of price. This reduces the impact of volatility and removes emotional decisions." },
  { title: "Don't Check Daily", content: "Checking your portfolio daily leads to emotional decisions. Weekly or monthly reviews are enough for long-term investors." },
  { title: "Diversify Globally", content: "Don't put all your money in Canadian stocks. Consider US (VFV), international (VIU), and emerging markets (VEE)." },
  { title: "Reinvest Dividends", content: "Turn on DRIP (Dividend Reinvestment Plan) to automatically buy more shares with your dividends. Free compound growth!" },
  { title: "Avoid FOMO", content: "When everyone is talking about a 'hot stock', it's often too late. Stick to your long-term plan." },
  { title: "Read Before Buying", content: "Understand what you're investing in. For ETFs, check the top holdings, fees (MER), and historical performance." },
  { title: "Tax-Loss Harvesting", content: "Selling losing investments to offset capital gains can reduce your tax bill. But watch out for superficial loss rules!" },
  { title: "Max Out Your TFSA", content: "Canadians get $7,000 TFSA room annually (2024). If you haven't contributed since 18, you might have $88,000+ room!" },
  { title: "Start Small, Learn Big", content: "It's okay to start with $100. The goal is building the habit and learning. You can increase amounts as you earn more." },
  { title: "Avoid High-Interest Debt", content: "Credit card debt at 20% interest beats any investment return. Pay off high-interest debt before investing aggressively." },
  { title: "Set Goals", content: "Define what you're investing for: retirement, house down payment, travel. Goals make it easier to stay disciplined." },
  { title: "Beware of Crypto Hype", content: "Crypto can be part of a portfolio, but it's highly volatile. Never invest more than you can afford to lose completely." },
  { title: "Use Registered Accounts", content: "Always invest in TFSA or RRSP before non-registered accounts. The tax savings are significant over time." },
  { title: "Rebalance Annually", content: "If stocks grow faster than bonds, your portfolio drifts from your target. Rebalance once a year to maintain your risk level." },
  { title: "Ignore Market News", content: "Daily market news creates anxiety. Focus on your 10+ year plan, not what happened today." },
  { title: "Learn from Mistakes", content: "Everyone makes investing mistakes. The key is learning from them and not repeating. Keep an investment journal." },
  { title: "Automate Everything", content: "Set up automatic deposits, automatic purchases, and dividend reinvestment. Remove the friction of manual investing." },
  { title: "Consider Asset Allocation", content: "A common rule: subtract your age from 100 to get your stock percentage. 25 years old = 75% stocks, 25% bonds." },
  { title: "Don't Sell in Panic", content: "Market crashes are the worst time to sell. Historically, markets recover. Those who stayed invested came out ahead." },
  { title: "Watch Out for Fees", content: "Bank mutual funds often charge 2%+ MER. Questrade, Wealthsimple, or self-directed ETF investing costs 0.2% or less." },
  { title: "Build Multiple Income Streams", content: "Dividends, rental income, side hustles. Don't rely solely on one income source for financial security." },
  { title: "Stay Curious", content: "The best investors never stop learning. Read books, follow finance creators, and keep educating yourself." },
];

const AdviceCarousel = ({ className = "" }) => {
  const [tips, setTips] = useState(DEFAULT_TIPS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-rotate every 3 minutes (180000ms)
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % tips.length);
    }, 180000); // 3 minutes
    
    return () => clearInterval(interval);
  }, [tips.length, isPaused]);

  // Try to load AI-generated tips on mount (optional)
  useEffect(() => {
    loadAITips();
  }, []);

  const loadAITips = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tips/carousel`);
      if (response.data.tips && response.data.tips.length > 0) {
        setTips(response.data.tips);
      }
    } catch (error) {
      // Silently fail and use default tips
      console.log("Using default tips");
    } finally {
      setLoading(false);
    }
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % tips.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + tips.length) % tips.length);
  };

  const currentTip = tips[currentIndex];

  return (
    <div 
      className={`relative ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="advice-carousel"
    >
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            {loading ? (
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <h4 className="font-semibold text-emerald-800 text-sm sm:text-base">
                  💡 {currentTip.title}
                </h4>
                <p className="text-emerald-700 text-xs sm:text-sm mt-1 leading-relaxed">
                  {currentTip.content}
                </p>
              </motion.div>
            </AnimatePresence>
            
            {/* Progress indicator */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex gap-1">
                {[...Array(Math.min(5, tips.length))].map((_, idx) => {
                  const dotIndex = Math.floor(currentIndex / (tips.length / 5));
                  return (
                    <div
                      key={idx}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        idx === dotIndex % 5 ? 'bg-emerald-500' : 'bg-emerald-200'
                      }`}
                    />
                  );
                })}
              </div>
              
              {/* Navigation buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrev}
                  className="p-1 hover:bg-emerald-200 rounded-full transition-colors"
                  aria-label="Previous tip"
                >
                  <ChevronLeft className="w-4 h-4 text-emerald-600" />
                </button>
                <span className="text-xs text-emerald-600 font-medium">
                  {currentIndex + 1}/{tips.length}
                </span>
                <button
                  onClick={goToNext}
                  className="p-1 hover:bg-emerald-200 rounded-full transition-colors"
                  aria-label="Next tip"
                >
                  <ChevronRight className="w-4 h-4 text-emerald-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdviceCarousel;
